/**
 * What the message form sends, what it refuses, and what the server's refusals mean — `W4-T06`.
 *
 * Pure, like `quote-form.ts`: the route actions are plumbing, and the rules are here. Keys, never
 * sentences.
 */
import { MESSAGE_BODY_MAX } from '@marketplace/contracts';
import { ApiError } from '../../shared/api-error.js';

export type MessageFormResult = { ok: true; body: string } | { ok: false; error: string };

/** What a message action answers — through a fetcher, so it never collides with the page's own. */
export type MessageActionData = { sent: true } | { messageError: string };

export function parseMessageForm(form: FormData): MessageFormResult {
  const raw = form.get('body');
  const body = typeof raw === 'string' ? raw.trim() : '';
  if (body === '') return { ok: false, error: 'thread.error.empty' };
  if (body.length > MESSAGE_BODY_MAX) return { ok: false, error: 'thread.error.tooLong' };
  return { ok: true, body };
}

/**
 * A refusal, as the sentence the person reads. `409` is a closed conversation — the job was
 * cancelled, or it is the provider's own — and `404` is a job that left their reach meanwhile.
 */
export function messageFailureKey(error: unknown): string {
  if (!(error instanceof ApiError)) return 'thread.error.failed';
  if (error.status === 409) return 'thread.error.closed';
  if (error.status === 404) return 'thread.error.gone';
  if (error.status === 400) return 'thread.error.tooLong';
  return 'thread.error.failed';
}

/** The message action both pages share, after they have picked which call sends it. */
export async function sendMessage(
  form: FormData,
  send: (body: string) => Promise<unknown>,
): Promise<MessageActionData> {
  const parsed = parseMessageForm(form);
  if (!parsed.ok) return { messageError: parsed.error };
  try {
    await send(parsed.body);
  } catch (error) {
    return { messageError: messageFailureKey(error) };
  }
  return { sent: true };
}
